import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { PaginatedResponse } from "../types";

import { EntityType, GetChangelogResponse } from "./types";

import { API_URL } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

interface UseGetChangelogPendingCountParams {
  entityId: number;
  entityType: EntityType;
  enabled?: boolean;
}

export const useGetChangelogPendingCount = ({
  entityId,
  entityType,
  enabled = true,
}: UseGetChangelogPendingCountParams) => {
  const { workspaceId } = useParams();
  const authContext = useAuth();

  return useQuery<number>({
    queryKey: ["changelog-pending-count", workspaceId, entityId, entityType],
    queryFn: async () => {
      const url = new URL(`${API_URL}/api/dashboard/v2/workspaces/${workspaceId}/changelog`);

      url.searchParams.set("entity_id", String(entityId));
      url.searchParams.set("entity_type", entityType);
      url.searchParams.set("page", "1");
      url.searchParams.set("per_page", "1");
      url.searchParams.set("include_unapproved", "true");
      url.searchParams.set("pending", "true");

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: jsonAuthHeaders(authContext?.userToken ?? ""),
      });

      const data: PaginatedResponse<GetChangelogResponse["items"], "items", string> =
        await handleResponse(response);

      return data.total ?? 0;
    },
    enabled: enabled && !!authContext?.userToken && !!workspaceId && !!entityId,
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
  });
};
